import { Box, SxProps } from '@mui/material';
import { ResourceContent as RawResourceContent } from '@protos/content_management/content.common.apis_pb';
import {
  Content,
  ContentVideoContentModel,
  ExperimentContentModel,
  ImplementedContentModel,
  PageContentInfo,
  PassageContentModel,
  PracticalContentModel,
  Question,
  Question_QuestionEnum,
  Resource_ResourceEnum,
  ResourceContent,
  ResourceTeacherInstruction,
} from '@protos/content_management/content.db_pb';
import React, { useEffect, useRef, useState } from 'react';
import { pxToRem, pxTovW } from '../../../commonUtils/resizeUtils';
import { IStyles } from '../../../commonUtils/styleUtils';
import {
  QuestionContainerWithSolution,
  QuestionEventCollection,
} from '../Question/QuestionContainerWithSolution';
import { deepClone } from '../Question/question-utils';
import { SelectedResourceInfo } from '../VideoPlayerWrapper/VideoPlayerWrapper';
import { ContentVideoContent } from './ContentVideoContent';
import { ExperimentContent } from './ExperimentContent';
import { PassageElement } from './PassageElement';
import { trackEvent } from '@event-tracking';
import { PracticalElement } from './PracticalElement';
import { ResourceTeacherInstructionElement } from './ResourceTeacherInstructionElement';
import { ContentIdAttempt, getLastAttemptedResponse } from './interfaces';
import {
  GeneoIotError,
  GeneoIotHandle,
  GeneoIotProjectFile,
  GeneoIotProjectMeta,
} from '../GeneoIotFrame';
import { ingestIotTelemetry } from './iotTelemetry';

const styles: IStyles = {
  elementsWrapper: {
    paddingX: {
      xs: pxToRem(15),
      md: pxToRem(40),
      lg: pxTovW(427),
    },
    marginTop: {
      xs: pxToRem(20),
      md: pxTovW(15),
    },
  },
};

interface IProps {
  pageNumber: number;
  pageContents?: PageContentInfo[];
  contentIdAttempt?: ContentIdAttempt;
  handleAnswerSubmit: (contentId: string, answer: string[]) => void;
  selected_resource_info?: SelectedResourceInfo;
  customStyle?: SxProps;
  showSubmitButton: boolean;
  eventCollection?: QuestionEventCollection;
  questionTypeDisplay?: boolean;
  instuctionsDisplay?: boolean;
  resource_raw_data?: RawResourceContent;
  hideAnswerAnimation?: boolean;
  disableDownload?: boolean;
  allowUserControls?: boolean;
  /** GIL feature flag (Feature.PROJECT_ASSESSMENT) - practicals and multi-language video render only when true. */
  isProjectAssessmentEnabled?: boolean;
  practical_project?: GeneoIotProjectFile;
  practical_meta?: GeneoIotProjectMeta;
  practicalReadOnly?: boolean;
  practicalSeedFromAuthoredProject?: boolean;
  practicalFrameRef?: React.Ref<GeneoIotHandle>;
  /** Rendered above the practical's simulator, e.g. the project's resource list. */
  practical_resource_menu?: React.ReactNode;
  onPracticalProject?: (project: GeneoIotProjectFile) => void;
  onPracticalDirtyChange?: (dirty: boolean) => void;
  onPracticalError?: (error: GeneoIotError) => void;
}
const getAttemptedAnswerForContentId = (
  contents: ImplementedContentModel[],
  contentIdAttempt?: ContentIdAttempt
) => {
  const contentAttempt = contentIdAttempt || {};
  const val: {
    [contentId: string]: string[];
  } = contents.reduce((obj, item) => {
    const contentValue = item.model.value;
    const contentId =
      contentValue instanceof Content
        ? contentValue.contentId
        : contentValue instanceof Question
        ? contentValue.questionId
        : contentValue instanceof ResourceTeacherInstruction
        ? contentValue.resourceId
        : undefined;
    if (contentId) {
      const responses = contentAttempt[contentId]?.responses;
      return {
        ...obj,
        [contentId]: getLastAttemptedResponse(responses)?.answer || [],
      };
    }
    return obj;
  }, {});
  return val;
};

export const ResourcePageContent = (props: IProps) => {
  const {
    pageContents,
    pageNumber,
    contentIdAttempt,
    handleAnswerSubmit,
    selected_resource_info,
    customStyle,
    showSubmitButton,
    eventCollection,
    questionTypeDisplay,
    instuctionsDisplay,
    resource_raw_data,
    hideAnswerAnimation,
    disableDownload,
    allowUserControls,
    isProjectAssessmentEnabled = false,
    practical_project,
    practical_meta,
    practicalReadOnly,
    practicalSeedFromAuthoredProject,
    practicalFrameRef,
    practical_resource_menu,
    onPracticalProject,
    onPracticalDirtyChange,
    onPracticalError,
  } = props;

  const openPdfInNewTab =
    resource_raw_data?.resourceType ===
      Resource_ResourceEnum.RESOURCE_TYPE_ADMIN_UPLOAD ||
    resource_raw_data?.resourceType ===
      Resource_ResourceEnum.RESOURCE_TYPE_TEACHER_UPLOAD;

  /**
   * The last project the frame pushed, so `gil.project_saved` can carry it.
   *
   * A ref, and separate from the `practical_project` prop: that prop is what
   * the HOST has persisted, which on the save path is one round trip behind
   * the editor, and in the learn flow is never set at all. This is what the
   * frame actually emitted.
   */
  const lastPushedProjectRef = useRef<GeneoIotProjectFile | undefined>(
    undefined
  );

  const handlePracticalProject = (project: GeneoIotProjectFile) => {
    lastPushedProjectRef.current = project;
    onPracticalProject?.(project);
  };

  const pageContent = pageContents ? pageContents[pageNumber] : undefined;
  const contents = pageContent?.contents || [];
  const [answers, setAnswers] = useState<{
    [contentId: string]: string[];
  }>({});

  const questionIds = pageContents?.reduce(
    (acc, item) => acc.concat(item.questionIds),
    [] as string[]
  );

  useEffect(() => {
    setAnswers(getAttemptedAnswerForContentId(contents, contentIdAttempt));
  }, [pageNumber]);

  const renderContentType = (content: Content) => {
    const con = content.content;
    if (!con) {
      return null;
    }
    const contentCase = con.model.case;
    const value = con.model.value;
    switch (contentCase) {
      case 'passage':
        if (value instanceof PassageContentModel) {
          return (
            <PassageElement
              resourceCategory={resource_raw_data?.resourceCategory}
              passage={value}
              disableDownload={disableDownload}
              allowUserControls={allowUserControls}
              openPdfInNewTab={openPdfInNewTab}
            />
          );
        }
        return null;
      case 'practical':
        if (
          isProjectAssessmentEnabled &&
          value instanceof PracticalContentModel
        ) {
          return (
            <PracticalElement
              key={content.contentId}
              practical={value}
              // Rides on the same fetchResourceContent payload as the rest of
              // the raw data, and is only set there when the caller sent the
              // project's assessmentId - a resource opened outside a project
              // has no teacher solution to show.
              teacherSolution={resource_raw_data?.teacherSolution}
              title={content.title || resource_raw_data?.title}
              savedProject={practical_project}
              meta={practical_meta}
              readOnly={practicalReadOnly}
              seedFromAuthoredProject={practicalSeedFromAuthoredProject}
              resourceMenu={practical_resource_menu}
              frameRef={practicalFrameRef}
              onReady={() =>
                // The GIL coding editor is open and usable.
                trackEvent(
                  'gil.coding_editor.opened',
                  {
                    resourceId: selected_resource_info?.resourceId,
                    contentId: content.contentId,
                    pageNumber: selected_resource_info?.pageNumber,
                    readOnly: practicalReadOnly ?? true,
                    board: practical_project?.board,
                    hasSavedProject: !!practical_project,
                  },
                  { resourceId: selected_resource_info?.resourceId }
                )
              }
              telemetry={{ enabled: true }}
              onTelemetry={(events) =>
                ingestIotTelemetry(events, {
                  resourceId: selected_resource_info?.resourceId,
                  contentId: content.contentId,
                  getProject: () => lastPushedProjectRef.current,
                })
              }
              onProject={handlePracticalProject}
              onDirty={onPracticalDirtyChange}
              onError={onPracticalError}
            />
          );
        }
        return null;
      case 'contentVideo':
        if (value instanceof ContentVideoContentModel) {
          return (
            <ContentVideoContent
              contentVideo={value}
              selected_resource_info={selected_resource_info}
              resourceCategory={resource_raw_data?.resourceCategory}
              disableDownload={disableDownload}
              allowUserControls={allowUserControls}
              openPdfInNewTab={openPdfInNewTab}
              isMultiLanguageEnabled={isProjectAssessmentEnabled}
            />
          );
        }
        return null;
      case 'experiment':
        if (value instanceof ExperimentContentModel) {
          return (
            <ExperimentContent
              experiment={value}
              resourceCategory={resource_raw_data?.resourceCategory}
              disableDownload={disableDownload}
              allowUserControls={allowUserControls}
              openPdfInNewTab={openPdfInNewTab}
            />
          );
        }
        return null;
      case 'flashCard':
      case 'mindMap':
      default:
        return null;
    }
    return null;
  };

  const getQuestionNumber = (questionId: string) => {
    const questionIndex = questionIds?.findIndex((qId) => qId === questionId);
    if (questionIndex === undefined) {
      return '';
    }
    return questionIndex + 1;
  };

  const renderPageContent = (
    content: ImplementedContentModel,
    contentIndex: number
  ) => {
    const contentType = content.model.case;
    const contentValue = content.model.value;
    const contentId =
      contentValue instanceof Content
        ? contentValue.contentId
        : contentValue instanceof Question
        ? contentValue.questionId
        : contentValue instanceof ResourceTeacherInstruction
        ? contentValue.resourceId
        : undefined;
    if (contentId == undefined) {
      return null;
    }
    const studentResponse = contentIdAttempt
      ? contentIdAttempt[contentId]
      : undefined;
    const lastResponse = getLastAttemptedResponse(studentResponse?.responses);
    const isSubmitted = !!(
      lastResponse && Object.keys(lastResponse).includes('answer')
    );
    switch (contentType) {
      case 'question':
        if (contentValue instanceof Question) {
          return (
            <Box sx={customStyle ? customStyle : styles.elementsWrapper}>
              <QuestionContainerWithSolution
                resourceCategory={resource_raw_data?.resourceCategory}
                showHintIcon
                showAnswerAnimation={!hideAnswerAnimation}
                question={contentValue}
                showSubmitButton={showSubmitButton}
                isSubmitted={isSubmitted}
                disableInput={isSubmitted}
                showAnswer={isSubmitted}
                handleNext={() =>
                  handleAnswerSubmit &&
                  handleAnswerSubmit(contentId, answers[contentId] || [])
                }
                questionNumber={getQuestionNumber(contentId)}
                handleAnswerChange={(val: string, ind?: number) => {
                  let answersCopy = (answers[contentId] || []).slice();
                  switch (contentValue.questionType) {
                    case Question_QuestionEnum.QUESTION_TYPE_MCQM:
                      if (!answersCopy.includes(val)) {
                        answersCopy.push(val);
                      } else {
                        answersCopy = answersCopy.filter((v) => v !== val);
                      }
                      break;
                    case Question_QuestionEnum.QUESTION_TYPE_FIB:
                      if (typeof ind == 'number') {
                        if (ind >= 1) {
                          for (let i = 0; i < ind; i++) {
                            if (!answersCopy[i]) {
                              answersCopy[i] = '';
                            }
                          }
                        }
                        answersCopy[ind] = val;
                      }
                      break;
                    case Question_QuestionEnum.QUESTION_TYPE_MCQS:
                    case Question_QuestionEnum.QUESTION_TYPE_TF:
                      answersCopy = [val];
                      break;
                  }
                  const answerObjectClone = deepClone(answers);
                  answerObjectClone[contentId] = answersCopy;
                  setAnswers(answerObjectClone);
                }}
                userAttemptedAnswer={answers[contentId] || []}
                selected_resource_info={selected_resource_info}
                eventCollection={eventCollection}
                questionTypeDisplay={questionTypeDisplay}
                instuctionsDisplay={instuctionsDisplay}
                imageResizeEnabled={true}
              />
            </Box>
          );
        }
        return null;
      case 'instruction':
        if (contentValue instanceof ResourceTeacherInstruction) {
          return (
            <Box sx={customStyle ? customStyle : styles.elementsWrapper}>
              <ResourceTeacherInstructionElement
                teacherInstruction={contentValue}
                resourceCategory={resource_raw_data?.resourceCategory}
                disableDownload={disableDownload}
                allowUserControls={allowUserControls}
                openPdfInNewTab={openPdfInNewTab}
              />
            </Box>
          );
        }
        return null;
      case 'content':
        if (contentValue instanceof Content) {
          // The simulator sizes itself to the viewport, so it renders outside
          // the reading-column wrapper the text content types share.
          return contentValue.content?.model.case === 'practical' ? (
            renderContentType(contentValue)
          ) : (
            <Box sx={customStyle ? customStyle : styles.elementsWrapper}>
              {renderContentType(contentValue)}
            </Box>
          );
        }
        return null;
      default:
        return null;
    }
  };

  const renderContents = (contents?: ImplementedContentModel[]) => {
    if (!contents) {
      return null;
    }
    return contents.map((content, index) => (
      <React.Fragment key={'page_' + pageNumber + '_content_' + index}>
        {renderPageContent(new ImplementedContentModel(content), index)}
      </React.Fragment>
    ));
  };

  return <>{renderContents(contents)}</>;
};

export default ResourcePageContent;
